/**
 * Governance Color Palette
 * Semantic color mapping for the Agentomy Command Center.
 * All UI components should reference these constants rather than using raw hex values.
 *
 * Canonical brand: "Governance Modernism" -- warm copper on near-black.
 *
 * SETTLED 2026-07-28. The brand handoff specifies `quarantined` twice and the two
 * disagree: its status table groups "blocked / critical / quarantined" onto #F87171,
 * while its Agent-Registry mock calls Quarantined amber #FBBF24. Neither is usable.
 * Collapsing it into critical erases a distinction this UI actually draws -- the domain
 * models `quarantined` as its own state, separate from `blocked` and `denied` -- and
 * amber is already `warning`.
 *
 * The interim value (#D4825F) was worse than either: it is byte-identical to
 * --accent-primary-text in ui/globals.css and is the endpoint of the brand gradient,
 * so the colour meaning "this agent is contained" WAS the brand accent.
 *
 * Chosen by measurement rather than by eye. Candidates were scored on the minimum
 * CIELAB dE against every colour a status must stay distinguishable from (the other
 * four states, the accent, the accent hover, the background, the body text):
 *
 *     #D4825F (interim)      dE  0.0  -- identical to the accent hover
 *     #B84C3E destructive    dE 17.0  -- too near the copper accent
 *     #A78BFA violet         dE 31.3  -- CHOSEN
 *     #A855F7 purple         dE 63.6  -- most distinct, but reads cyberpunk,
 *                                        which is the cruft this reskin removes
 *
 * #A78BFA is decisively separable (dE 31.3, well above the ~12 confusion threshold)
 * while staying muted enough for warm copper on near-black, and it contrasts 6.43:1
 * against --bg-base -- inside the 6.3-10.5:1 band the other four states occupy, so no
 * state reads heavier than its severity warrants.
 */
export const GovernanceColors = {
  // Trust anchor -- the brand accent (Governance Modernism copper)
  primary: '#C2714F',        // Copper - trust, authority
  primaryLight: '#D4825F',   // Copper hover

  // Status. These are the semantic signals and the copper accent does NOT
  // swallow them -- green/amber/red/info stay distinct per the brand guide.
  // `governed` was removed 2026-07-28. It was an exact duplicate of `primary` (#C2714F,
  // the brand accent), consumed by nothing, and "governed" is not one of the states the
  // domain actually models (active / monitored / quarantined / blocked / denied / halted
  // / frozen). A status-sounding name holding the accent value is the precise trap that
  // produced the quarantine defect above -- paint a state with it and the state renders
  // as brand chrome. If a governed badge is ever needed, give it a measured distinct
  // value then; do not pre-seed an accent alias under a state name.
  warning: '#FBBF24',        // Amber - flagged / monitored
  critical: '#F87171',       // Red - blocked / critical
  healthy: '#34D399',        // Green - active / allowed (status only, not accent)
  ungoverned: '#8F857B',     // Dim - not yet governed
  quarantined: '#A78BFA',    // Violet - contained, not failing. See header note.

  // Functional
  streaming: '#60A5FA',      // Info blue - live/streaming indicator only
  background: '#1C1917',     // App background (near-black warm)
  surface: '#292524',        // Card/panel surface
  text: '#F5F0E8',           // Primary text (cream)
  textMuted: '#A8A29E',      // Secondary text
} as const;

export type GovernanceColorKey = keyof typeof GovernanceColors;

/**
 * Trust Budget gauge color based on current level (0-100).
 */
export function getTrustBudgetColor(level: number): string {
  if (level <= 25) return GovernanceColors.critical;
  if (level <= 50) return GovernanceColors.warning;
  return GovernanceColors.primary;
}
